import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Copy, Download, FileSearch, Triangle } from 'lucide-react'
import EmployeeTransactionsModal from './EmployeeTransactionsModal'
import { useAuthStore } from '../../stores/authStore'
import {
    BUCKS_RATE_OUTLIER_MULTIPLE,
    LOYALTY_AUDIT_PERMISSION,
    auditNarrowsStores,
    buildBucksAuditUrl,
    bucksRateMedian,
    canAuditChainWide,
    describeEmployeePerformanceError,
    employeeColumnValue,
    employeeInitials,
    formatEmployeeValue,
    isBucksRateOutlier,
    nextEmployeeSort,
    resolveEmployeeSettings,
    sortEmployees,
    storeSelectionLabel,
    visibleEmployeeColumns,
    type EmployeeColumnDef,
    type EmployeeColumnKey,
    type EmployeePerformanceData,
    type EmployeePerformanceQueryContext,
    type EmployeePerformanceRow,
    type EmployeePerformanceSettings,
    type SortDir,
} from '../../lib/employeePerformance'
import { buildEmployeeExport, downloadCsv, employeeExportFilename, toCsv, toTsv } from '../../lib/employeePerformanceExport'

// ── Employee Performance widget: the table ──────────────────────────────
//
// Sales, discounts and Adventure Bucks per employee for whatever the dashboard is showing. Column
// order, visibility, the saved sort and the options come from the widget's settings (edited in
// EmployeePerformanceConfigModal); clicking a header re-sorts for this viewing only — the saved
// sort comes back whenever the settings change it, and nothing a header click does is persisted.
//
// Outside Edit Layout the title bar carries Copy and Export CSV — every employee, in the columns on
// screen, their order and the current sort, plus a totals row (lib/employeePerformanceExport) — and
// each employee opens the receipts behind their row (EmployeeTransactionsModal). In Edit Layout the
// card is there to be dragged, so neither is offered: nothing on it may start a download or open a
// dialog when someone presses on it to move the widget.
//
// Anyone who may open the Adventure Bucks audit also gets an icon on each employee that opens it
// filtered to that person (see buildBucksAuditUrl for what the audit can and can't match). It keeps
// the same rule: not offered in Edit Layout, nor over stand-in figures.

export interface EmployeePerformancePanelProps {
    data: EmployeePerformanceData | undefined
    settings: EmployeePerformanceSettings | undefined
    /** e.g. 'Today', 'Sep 12', 'September 2026', 'Aug 1 – Aug 31, 2026'. */
    periodLabel: string
    /** 'all' | 'instore' | 'online'. */
    channel: string
    /** NET (true) / GROSS (false) — renames the sales column the way the Net Sales card is renamed. */
    includeReturns: boolean
    /** 'group' | 'all' | a store GUID. */
    selectedStoreId: string
    /** True while there is no data yet. */
    isLoading: boolean
    /** The react-query error (an EmployeePerformanceError when the server answered), or null. */
    error: unknown
    /**
     * The period and filters these figures were asked for with, and the page's query key. The
     * drill-down asks for an employee's receipts with exactly these, and the export's file name
     * names the dashboard from them. Without it there is no drill-down.
     */
    query?: EmployeePerformanceQueryContext
    /** The store selector's wording ('Calgary', 'My Store Group', 'All Stores'), for the file name and the drill-down. */
    storeLabel?: string
    /** Edit Layout is on: no Copy, Export or drill-down. */
    isEditing?: boolean
    /** The figures on screen are the last selection's, standing in while the new ones load: nothing is handed out. */
    isStale?: boolean
}

const ONLINE_BUCKS_NOTE = 'Online orders don’t earn Adventure Bucks at the till.'
const COPY_FEEDBACK_MS = 2000

/** The columns worked out from Adventure Bucks, which the online-orders note speaks for. */
const isBucksColumn = (key: EmployeeColumnKey) => key === 'bucks' || key === 'bucksRate'

const AUDIT_CAVEAT = 'The audit lists only flagged credits, and counts each one for whoever applied it, '
    + 'on the day it was applied — not for whoever rang the sale.'

const channelSuffix = (channel: string) =>
    channel === 'instore' ? 'In-Store' : channel === 'online' ? 'Online' : null

const ariaSortFor = (dir: SortDir) => (dir === 'asc' ? 'ascending' : 'descending')

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
    const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown
    return <Icon size={12} aria-hidden="true" className="emp-perf-sort-icon" />
}

export default function EmployeePerformancePanel({
    data, settings, periodLabel, channel, includeReturns, selectedStoreId, isLoading, error,
    query, storeLabel, isEditing = false, isStale = false,
}: EmployeePerformancePanelProps) {
    const resolved = useMemo(() => resolveEmployeeSettings(settings), [settings])
    const columns = useMemo(() => visibleEmployeeColumns(resolved.columns), [resolved.columns])
    const ctx = { includeReturns }
    // Subscribed rather than read once, so the icons follow the signed-in user — an impersonation
    // starting or ending included.
    const canAudit = useAuthStore(s => s.hasPermission(LOYALTY_AUDIT_PERMISSION))
    const chainWideAudit = useAuthStore(s => canAuditChainWide(s.user?.role))
    const uid = useId()

    // A header click sorts for this viewing only. The saved sort is the baseline, and whenever the
    // settings change it (the column editor's Apply, a layout load) the local choice is dropped so the
    // table shows what was just configured. Adjusting state during render, not in an effect, means the
    // stale order is never painted.
    const basis = `${resolved.sortKey}:${resolved.sortDir}`
    const [picked, setPicked] = useState<{ key: EmployeeColumnKey; dir: SortDir }>({ key: resolved.sortKey, dir: resolved.sortDir })
    const [sortBasis, setSortBasis] = useState(basis)
    if (sortBasis !== basis) {
        setSortBasis(basis)
        setPicked({ key: resolved.sortKey, dir: resolved.sortDir })
    }
    // A header sort also lasts only while its column is on screen. Hide that column in the editor and
    // the saved sort takes over, rather than the rows (and their rank numbers) staying ordered by a
    // figure nobody can see, with no header marked. The pick is dropped, not just masked, so showing
    // the column again doesn't re-order the table without a click. It can't loop: when the saved
    // sort's own column is hidden, picked already equals the saved sort.
    const pickedShown = columns.some(c => c.key === picked.key)
    if (!pickedShown && (picked.key !== resolved.sortKey || picked.dir !== resolved.sortDir)) {
        setPicked({ key: resolved.sortKey, dir: resolved.sortDir })
    }
    const sort = pickedShown ? picked : { key: resolved.sortKey, dir: resolved.sortDir }

    const sorted = useMemo(
        () => (data ? sortEmployees(data.employees, sort.key, sort.dir, data.totals) : []),
        [data, sort.key, sort.dir],
    )
    // Bars are scaled to the top seller across EVERY employee in the view, so a row keeps its bar
    // length when the row limit or the sort changes.
    const maxSales = useMemo(
        () => (data ? data.employees.reduce((m, r) => (r.netSales > m ? r.netSales : m), 0) : 0),
        [data],
    )

    // The outlier marker's baseline, like the bars, is every employee in the view — not just the rows
    // the limit leaves on screen — so a row's marker doesn't come and go with the sort or the limit.
    const rateShown = columns.some(c => c.key === 'bucksRate')
    const rateMedian = useMemo(
        () => (data && rateShown ? bucksRateMedian(data.employees) : null),
        [data, rateShown],
    )

    const limited = resolved.rowLimit > 0 && sorted.length > resolved.rowLimit
    const shown = limited ? sorted.slice(0, resolved.rowLimit) : sorted
    const multiStore = selectedStoreId === 'group' || selectedStoreId === 'all'
    const bucksVisible = columns.some(c => isBucksColumn(c.key))
    const onlineNote = channel === 'online' && bucksVisible
    const flagNote = rateMedian !== null && shown.some(r => isBucksRateOutlier(r, rateMedian))
    const medianText = formatEmployeeValue('currencyCents', rateMedian)
    const suffix = channelSuffix(channel)
    const count = data?.employees.length
    const where = storeLabel ?? storeSelectionLabel(selectedStoreId, undefined)

    // ── Copy and Export CSV ──
    const hasRows = !error && !!data && data.employees.length > 0
    const exportable = hasRows && !isStale
    const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
    const [announcement, setAnnouncement] = useState('')
    const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    useEffect(() => () => clearTimeout(copyTimer.current), [])

    const exportTable = () => buildEmployeeExport(data!, columns, sort, ctx)

    const handleCopy = async () => {
        if (!exportable) return
        clearTimeout(copyTimer.current)
        const n = data!.employees.length
        try {
            if (!navigator.clipboard) throw new Error('No clipboard in this browser context')
            await navigator.clipboard.writeText(toTsv(exportTable()))
            setCopyState('copied')
            setAnnouncement(`Copied ${n} ${n === 1 ? 'employee' : 'employees'} and the totals, ready to paste into a spreadsheet.`)
        } catch {
            setCopyState('failed')
            setAnnouncement('The table could not be copied to the clipboard.')
        }
        copyTimer.current = setTimeout(() => setCopyState('idle'), COPY_FEEDBACK_MS)
    }

    const handleExport = () => {
        if (!exportable) return
        const filename = employeeExportFilename({
            period: query?.period, startDate: data!.startDate, endDate: data!.endDate, storeLabel: where, channel,
        })
        downloadCsv(filename, toCsv(exportTable()))
        setAnnouncement(`Downloaded ${filename}.`)
    }

    // ── Drill-down ──
    const drillable = !!query && !isEditing && !isStale && hasRows
    const [drill, setDrill] = useState<EmployeePerformanceRow | null>(null)
    const drillButtons = useRef(new Map<string, HTMLButtonElement>())
    // What a name's button does is its description, read out on the button — not text inside it: the
    // name cell is the row header, and whatever it holds is announced with every figure in the row.
    const drillHint = useId()

    const closeDrill = () => {
        const opener = drill ? drillButtons.current.get(drill.employeeId) : undefined
        setDrill(null)
        opener?.focus()   // back to the employee the keyboard user left from
    }

    /** Anywhere on an employee's row opens their receipts — unless the click ends a text selection. */
    const onRowClick = (row: EmployeePerformanceRow) => {
        if (window.getSelection?.()?.toString()) return
        setDrill(row)
    }

    const storeLine = (row: EmployeePerformanceRow) => {
        if (!row.homeStoreName) return null
        if (multiStore) return row.homeStoreName
        return (row.homeStoreId ?? '').toLowerCase() !== selectedStoreId.toLowerCase() ? row.homeStoreName : null
    }

    // ── The way into the Adventure Bucks audit ──
    // Withheld where the drill-down is: in Edit Layout a press starts a drag (and following the link
    // would lose the unsaved arrangement), and over stand-in figures the rows and dates on screen are
    // the last selection's while the store selector is already the new one. The row keeps the icon's
    // room meanwhile, so the names don't shift as the figures reload.
    const offerAudit = canAudit && !isEditing && !isStale
    const auditUrl = (row: EmployeePerformanceRow) =>
        canAudit && data
            ? buildBucksAuditUrl(row.employeeId, {
                storeId: selectedStoreId, startDate: data.startDate, endDate: data.endDate, chainWide: chainWideAudit,
            })
            : null
    const auditStores = auditNarrowsStores(selectedStoreId, chainWideAudit)
        ? 'your own stores — the audit doesn’t show you every store'
        : 'stores'

    const headerTitle = (def: EmployeeColumnDef) =>
        isBucksColumn(def.key) && channel === 'online' ? `${def.description} ${ONLINE_BUCKS_NOTE}` : def.description

    const numClass = (def: EmployeeColumnDef) =>
        def.numeric ? `emp-perf-num${def.key === 'sales' ? ' emp-perf-sales' : ''}` : undefined

    let body: ReactNode
    if (error) {
        body = <div className="emp-perf-state" role="status">{describeEmployeePerformanceError(error)}</div>
    } else if (!data) {
        body = <div className="emp-perf-state" role="status">{isLoading ? 'Loading employee figures…' : 'Waiting for employee figures…'}</div>
    } else if (data.employees.length === 0) {
        body = <div className="emp-perf-state" role="status">No sales recorded for this period</div>
    } else {
        body = (
            <div className="chart-panel-content">
                <table className="emp-perf-table">
                    <thead>
                        <tr>
                            {columns.map(def => {
                                const active = def.sortable && sort.key === def.key
                                return (
                                    <th
                                        key={def.key}
                                        scope="col"
                                        data-col={def.key}
                                        className={def.key === 'rank' ? 'emp-perf-rank' : numClass(def)}
                                        aria-sort={active ? ariaSortFor(sort.dir) : undefined}
                                        title={headerTitle(def)}
                                    >
                                        {def.sortable ? (
                                            <button type="button" className="emp-perf-sort" onClick={() => setPicked(nextEmployeeSort(sort, def.key))}>
                                                <span>{def.label(ctx)}</span>
                                                <SortIcon active={active} dir={sort.dir} />
                                            </button>
                                        ) : def.key === 'rank' ? (
                                            <><span aria-hidden="true">#</span><span className="sr-only">Rank</span></>
                                        ) : def.label(ctx)}
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((row, i) => (
                            <tr
                                key={row.employeeId}
                                className={[row.departed ? 'is-departed' : '', drillable ? 'is-drillable' : ''].filter(Boolean).join(' ') || undefined}
                                onClick={drillable ? () => onRowClick(row) : undefined}
                            >
                                {columns.map(def => {
                                    if (def.key === 'rank') return <td key="rank" className="emp-perf-rank">{i + 1}</td>
                                    if (def.key === 'employee') {
                                        const store = storeLine(row)
                                        const name = row.name.trim()
                                        const audit = auditUrl(row)
                                        const identId = `${uid}-${row.employeeId}`
                                        const nameText = (
                                            <span className={`emp-perf-name${name ? '' : ' is-unknown'}`} title={name || undefined}>{name || 'Unknown employee'}</span>
                                        )
                                        // The row header: a screen reader moving down a figure column
                                        // announces whose figure it is, so it is named by the identity alone —
                                        // never the audit link's label. The name is also the drill-down's
                                        // keyboard way in; a click anywhere on the row works for a mouse.
                                        return (
                                            <th key="employee" scope="row" className="emp-perf-employee" aria-labelledby={identId}>
                                                <div className="emp-perf-person">
                                                    <span className="emp-perf-avatar" aria-hidden="true">{employeeInitials(name)}</span>
                                                    <span className="emp-perf-ident" id={identId}>
                                                        <span className="emp-perf-line">
                                                            {drillable ? (
                                                                <button
                                                                    type="button"
                                                                    className="emp-perf-drill"
                                                                    aria-haspopup="dialog"
                                                                    aria-describedby={drillHint}
                                                                    ref={el => {
                                                                        if (el) drillButtons.current.set(row.employeeId, el)
                                                                        else drillButtons.current.delete(row.employeeId)
                                                                    }}
                                                                    onClick={e => { e.stopPropagation(); setDrill(row) }}
                                                                >
                                                                    {nameText}
                                                                </button>
                                                            ) : nameText}
                                                            {row.departed && <span className="emp-perf-tag" title="No longer with the company — these sales are still theirs">former</span>}
                                                        </span>
                                                        {store && <span className="emp-perf-sub">{store}</span>}
                                                    </span>
                                                    {/* Per-row actions have their own slot, apart from the name — the
                                                        drill-down's way in — so the two never share a click. */}
                                                    {audit && (
                                                        <span className="emp-perf-row-actions">
                                                            {offerAudit && (
                                                                <Link
                                                                    to={audit}
                                                                    className="emp-perf-row-action"
                                                                    aria-label={`Open the Adventure Bucks audit for ${name || 'Unknown employee'}`}
                                                                    title={`Adventure Bucks audit for ${name || 'Unknown employee'}, over these dates and ${auditStores}. ${AUDIT_CAVEAT}`}
                                                                    // The click is the link's alone: the row's own click opens the receipts.
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <FileSearch size={14} aria-hidden="true" />
                                                                </Link>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        )
                                    }
                                    const value = employeeColumnValue(def.key, row, data.totals)
                                    const text = formatEmployeeValue(def.format, value)
                                    if (def.key === 'bucksRate' && isBucksRateOutlier(row, rateMedian)) {
                                        // The triangle is the marker, so it never rests on colour; the tooltip is
                                        // for a mouse, the hidden sentence for a screen reader, and the legend in
                                        // the foot for everyone else.
                                        return (
                                            <td key="bucksRate" className={numClass(def)}>
                                                <span className="emp-perf-flag" aria-hidden="true"
                                                    title={`Unusually high: more than ${BUCKS_RATE_OUTLIER_MULTIPLE}× the median employee’s ${medianText} per $100`}>
                                                    <Triangle size={9} fill="currentColor" />
                                                </span>
                                                {text}
                                                <span className="sr-only">
                                                    {`, unusually high: more than ${BUCKS_RATE_OUTLIER_MULTIPLE} times the median employee’s ${medianText} per $100`}
                                                </span>
                                            </td>
                                        )
                                    }
                                    if (def.key === 'sales') {
                                        const share = maxSales > 0 ? Math.min(1, Math.max(0, row.netSales / maxSales)) : 0
                                        return (
                                            <td key="sales" className={numClass(def)}>
                                                <span className="emp-perf-value">{text}</span>
                                                {resolved.showBars && (
                                                    <span className="emp-perf-bar" aria-hidden="true">
                                                        <span className="emp-perf-bar-fill" style={{ width: `${(share * 100).toFixed(1)}%` }} />
                                                    </span>
                                                )}
                                            </td>
                                        )
                                    }
                                    return <td key={def.key} className={numClass(def)}>{text}</td>
                                })}
                            </tr>
                        ))}
                    </tbody>
                    {resolved.showTotals && (
                        <tfoot>
                            <tr>
                                {columns.map(def => {
                                    if (def.key === 'rank') return <td key="rank" className="emp-perf-rank" />
                                    if (def.key === 'employee') {
                                        return (
                                            <th key="employee" scope="row" className="emp-perf-employee">
                                                <span className="emp-perf-total-label">Total</span>
                                                {limited && <span className="emp-perf-sub">All {sorted.length} employees</span>}
                                            </th>
                                        )
                                    }
                                    const value = employeeColumnValue(def.key, data.totals, data.totals)
                                    return <td key={def.key} className={numClass(def)}>{formatEmployeeValue(def.format, value)}</td>
                                })}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        )
    }

    const showFoot = !error && !!data && data.employees.length > 0 && (limited || onlineNote || flagNote)

    return (
        <div className="chart-panel emp-perf" style={{ height: '100%' }}>
            <div className="chart-panel-title emp-perf-title">
                <span className="emp-perf-heading">
                    Employee Performance
                    <span className="emp-perf-meta">
                        {periodLabel && <> · {periodLabel}</>}
                        {suffix && <> · {suffix}</>}
                    </span>
                </span>
                <span className="emp-perf-title-end">
                    {count !== undefined && !error && (
                        <span className="emp-perf-count">{count} {count === 1 ? 'employee' : 'employees'}</span>
                    )}
                    {!isEditing && (
                        <span className="emp-perf-actions">
                            <button
                                type="button"
                                className="emp-perf-action"
                                disabled={!exportable}
                                title="Copy every employee and the totals, ready to paste into a spreadsheet"
                                onClick={handleCopy}
                            >
                                {copyState === 'copied' ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
                                <span className="emp-perf-action-text">
                                    {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Copy failed' : 'Copy'}
                                </span>
                            </button>
                            <button
                                type="button"
                                className="emp-perf-action"
                                disabled={!exportable}
                                title="Download every employee and the totals as a CSV file"
                                onClick={handleExport}
                            >
                                <Download size={13} aria-hidden="true" />
                                <span className="emp-perf-action-text">Export CSV</span>
                            </button>
                        </span>
                    )}
                </span>
            </div>
            {body}
            {showFoot && (
                <div className="emp-perf-foot">
                    {limited && <span>Showing top {shown.length} of {sorted.length}</span>}
                    {flagNote && (
                        <span className="emp-perf-flag-note">
                            <span className="emp-perf-flag" aria-hidden="true"><Triangle size={9} fill="currentColor" /></span>
                            Bucks/$100 more than {BUCKS_RATE_OUTLIER_MULTIPLE}× the median employee’s ({medianText})
                        </span>
                    )}
                    {onlineNote && <span>{ONLINE_BUCKS_NOTE}</span>}
                </div>
            )}
            <div className="sr-only" aria-live="polite">{announcement}</div>
            {/* Hidden, but still read as the names' description (aria-describedby reaches hidden text). */}
            {drillable && <span id={drillHint} hidden>Shows the receipts behind this employee’s figures.</span>}
            {drill && query && createPortal(
                <EmployeeTransactionsModal
                    employee={drill}
                    query={query}
                    periodLabel={periodLabel}
                    storeLabel={where}
                    onClose={closeDrill}
                />,
                document.body,
            )}
        </div>
    )
}
